import { ref } from 'vue';
import type {
	ViewModel,
	FacetGroupModel,
	DocumentRow,
	DocumentSearchRequest,
	DocumentSearchResponse,
} from '../DocumentSearch.types';

type Fetcher = (
	payload: DocumentSearchRequest
) => Promise<DocumentSearchResponse>;

export function useDocumentSearch(
	vm: ViewModel,
	searchArchived: boolean,
	fetcher?: Fetcher
) {
	const keyword = ref('');
	const filters = ref<FacetGroupModel[]>([]);
	const facetGroups = ref<FacetGroupModel[]>([]);
	const documents = ref<DocumentRow[]>([]);
	const isLoaded = ref(false);
	const isFetching = ref(false);

	const doFetch: Fetcher =
		fetcher ??
		(async (payload) => {
			const { default: axios } = await import('shared/tools/axiosMultisite');
			const { data } = await axios.post<DocumentSearchResponse>(
				'/api/document-search',
				payload
			);
			return data;
		});

	async function fetchData() {
		if (!keyword.value) {
			facetGroups.value = [];
			documents.value = [];
			return
		}

		isFetching.value = true;
		try {
			const payload: DocumentSearchRequest = {
				WebDocumentType: vm.BaseViewModel.WebDocumentType,
				Terms: keyword.value ? keyword.value.split(',') : [],
				PageSize: Number(vm.CurrentBlock.SearchResultPageSize),
				FacetGroups: filters.value,
				SearchForArchivedFiles: !!searchArchived,
				Language: vm.BaseViewModel.Language,
				CurrentPageId: vm.BaseViewModel.CurrentPageId,
			};

			const data = await doFetch(payload);
			facetGroups.value = data?.facetGroups ?? [];
			documents.value = data?.searchResultItems ?? [];
		} finally {
			isLoaded.value = true;
			isFetching.value = false;
		}
	}

	function applyFilters(facetGroup: FacetGroupModel) {
		const idx = filters.value.findIndex((f) => f.name === facetGroup.name);
		if (idx < 0) filters.value.push(facetGroup);
		else filters.value[idx] = facetGroup;
	}

	function resetFilters() {
		filters.value = [];
	}

	return {
		keyword,
		filters,
		facetGroups,
		documents,
		isLoaded,
		isFetching,
		fetchData,
		applyFilters,
		resetFilters,
	};
}
